import { platformJsonValueSchema, } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { projectsCountryCodeSchema, projectsDateSchema, projectsPriorityKeySchema, projectsProjectTypeKeySchema, projectsPropertyTypeKeySchema, projectsStatusKeySchema, } from './projects-project.contract.js';
export const projectsFullProjectImportCapability = 'projects.full-project-import.v1';
export const projectsFullProjectImportContractVersion = projectsFullProjectImportCapability;
export const projectsFullProjectImportBridgeContractVersion = 'projects.full-project-import-bridge.v1';
export const projectsFullProjectImportEntityType = 'project';
export const projectsFullProjectImportAllowedSourceStatusSchema = z.enum([
    'new',
    'scheduled',
    'in_progress',
    'drying',
    'pending_invoice',
    'pending_payment',
    'legacy_import',
    'completed',
    'closed',
    'cancelled',
    'estimate_sent',
    'waiting_customer',
    'on_hold',
    'estimating',
    'follow_up_required',
    'quality_review',
]);
export const projectsFullProjectImportStatusIdentityMap = {
    new: 'new',
    scheduled: 'scheduled',
    in_progress: 'in_progress',
    drying: 'drying',
    pending_invoice: 'pending_invoice',
    pending_payment: 'pending_payment',
    legacy_import: 'legacy_import',
    completed: 'completed',
    closed: 'closed',
    cancelled: 'cancelled',
    estimate_sent: 'estimate_sent',
    waiting_customer: 'waiting_customer',
    on_hold: 'on_hold',
    estimating: 'estimating',
    follow_up_required: 'follow_up_required',
    quality_review: 'quality_review',
};
const nullableText = (maximum) => z.string().max(maximum).nullable();
const importText = (maximum) => z.string().trim().min(1).max(maximum);
const importTimestamp = z.string().datetime({ offset: true });
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const jsonObjectSchema = z.record(z.string(), platformJsonValueSchema);
const relationshipsImportResolutionSchema = z.strictObject({
    capability: z.literal('relationships.import-resolution.v1'),
    entityType: z.enum(['organization', 'contact']),
    sourceId: importText(240),
    targetId: z.string().uuid(),
    sourceSha256: sha256Schema,
    sourceUpdatedAt: importTimestamp,
    runId: z.string().uuid(),
    targetSha256: sha256Schema,
});
export const projectsFullProjectImportWriterFenceSchema = z.strictObject({
    fenceEpoch: importText(200),
    sourceWriterState: z.enum(['authoritative', 'frozen']),
    targetWriterState: z.literal('disabled'),
    noDualWriters: z.literal(true),
    sourceThrough: importTimestamp,
    attestationSha256: sha256Schema,
    attestedAt: importTimestamp,
}).superRefine((value, context) => {
    if (Date.parse(value.attestedAt) < Date.parse(value.sourceThrough)) {
        context.addIssue({
            code: 'custom',
            path: ['attestedAt'],
            message: 'Writer fence attestation cannot predate sourceThrough.',
        });
    }
});
/**
 * Exact source row material for the first full-row Projects writer.
 * Customer snapshot and primary contact are intentionally excluded here and
 * must come from the explicit Relationships bridge input.
 */
export const projectsFullProjectImportSourceSchema = z.strictObject({
    org_id: z.string().trim().min(1).max(240).nullable(),
    project_number: z.string().max(200),
    title: z.string().max(500),
    status: z.string().trim().min(1).max(80),
    type: nullableText(200),
    property_type: nullableText(200),
    priority: nullableText(200),
    business_entity: nullableText(200),
    is_water_job: z.boolean().nullable(),
    property_address: nullableText(500),
    property_city: nullableText(160),
    property_state: nullableText(120),
    property_zip: nullableText(40),
    start_date: nullableText(40),
    target_completion_date: nullableText(40),
    created_at: importTimestamp,
    updated_at: importTimestamp,
});
export const projectsFullProjectImportCustomerSnapshotSchema = z.strictObject({
    displayName: z.string().trim().max(240).nullable(),
    phone: z.string().trim().max(80).nullable(),
    email: z.string().trim().email().max(320).nullable(),
    addressLine1: z.string().trim().max(240).nullable(),
    addressLine2: z.string().trim().max(240).nullable(),
    city: z.string().trim().max(120).nullable(),
    region: z.string().trim().max(120).nullable(),
    postalCode: z.string().trim().max(40).nullable(),
    countryCode: projectsCountryCodeSchema.nullable(),
});
export const projectsFullProjectImportLossSnapshotSchema = z.strictObject({
    incidentType: z.string().trim().max(100).nullable(),
    cause: z.string().trim().max(200).nullable(),
    lossTimestamp: importTimestamp.nullable(),
    affectedAreas: z.array(importText(100)).max(50),
});
export const projectsFullProjectImportBridgeProjectSchema = z.strictObject({
    sourceId: importText(500),
    customerSnapshot: projectsFullProjectImportCustomerSnapshotSchema,
    primaryContactSourceExternalKey: importText(240).nullable(),
    lossSnapshot: projectsFullProjectImportLossSnapshotSchema,
});
export const projectsFullProjectImportRelationshipResolutionSchema = z.strictObject({
    sourceExternalKey: importText(240),
    sourceTenantExternalKey: importText(240),
    resolution: relationshipsImportResolutionSchema,
    verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.sourceExternalKey !== value.resolution.sourceId) {
        context.addIssue({
            code: 'custom',
            path: ['resolution', 'sourceId'],
            message: 'Relationship resolution source ID must match the supplied source external key.',
        });
    }
});
export const projectsFullProjectImportBridgeInputSchema = z.strictObject({
    contractVersion: z.literal(projectsFullProjectImportBridgeContractVersion),
    projects: z.array(projectsFullProjectImportBridgeProjectSchema).max(100_000),
    relationshipResolutions: z.array(projectsFullProjectImportRelationshipResolutionSchema).max(100_000),
}).superRefine((bridge, context) => {
    const projectIds = bridge.projects.map(({ sourceId }) => sourceId);
    if (new Set(projectIds).size !== projectIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['projects'],
            message: 'Bridge Project source IDs must be unique.',
        });
    }
    const resolutionKeys = bridge.relationshipResolutions.map(({ resolution, sourceExternalKey }) => `${resolution.entityType}\u0000${sourceExternalKey}`);
    if (new Set(resolutionKeys).size !== resolutionKeys.length) {
        context.addIssue({
            code: 'custom',
            path: ['relationshipResolutions'],
            message: 'Relationship resolutions must be unique by entity type and source external key.',
        });
    }
});
export const projectsFullProjectImportCustomerPayloadSchema = z.strictObject({
    displayName: z.string().trim().max(240).nullable(),
    phone: z.string().trim().max(80).nullable(),
    email: z.string().trim().email().max(320).nullable(),
    addressLine1: z.string().trim().max(240).nullable(),
    addressLine2: z.string().trim().max(240).nullable(),
    city: z.string().trim().max(120).nullable(),
    region: z.string().trim().max(120).nullable(),
    postalCode: z.string().trim().max(40).nullable(),
    countryCode: projectsCountryCodeSchema.nullable(),
});
export const projectsFullProjectImportServiceAddressPayloadSchema = z.strictObject({
    line1: z.string().trim().max(240).nullable(),
    line2: z.string().trim().max(240).nullable(),
    city: z.string().trim().max(120).nullable(),
    region: z.string().trim().max(120).nullable(),
    postalCode: z.string().trim().max(40).nullable(),
    countryCode: projectsCountryCodeSchema,
});
export const projectsFullProjectImportSourceEvidenceSchema = z.strictObject({
    sourceStatus: projectsFullProjectImportAllowedSourceStatusSchema,
    sourceCreatedAt: importTimestamp,
    sourceRelationshipRefs: z.strictObject({
        organizationExternalKey: importText(240).nullable(),
        primaryContactExternalKey: importText(240).nullable(),
    }),
    deferredVerticalFields: jsonObjectSchema,
    businessEntity: z.string().trim().min(1).max(200).nullable(),
    lossSnapshot: projectsFullProjectImportLossSnapshotSchema,
});
export const projectsFullProjectImportTargetPayloadSchema = z.strictObject({
    projectNumber: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(240),
    status: projectsStatusKeySchema,
    projectType: projectsProjectTypeKeySchema,
    priority: projectsPriorityKeySchema,
    propertyType: projectsPropertyTypeKeySchema.nullable(),
    isWaterJob: z.boolean(),
    createdAt: importTimestamp,
    startDate: projectsDateSchema.nullable(),
    targetCompletionDate: projectsDateSchema.nullable(),
    organizationId: z.string().uuid().nullable(),
    primaryContactId: z.string().uuid().nullable(),
    countryCode: projectsCountryCodeSchema,
    customer: projectsFullProjectImportCustomerPayloadSchema,
    serviceAddress: projectsFullProjectImportServiceAddressPayloadSchema,
});
export const projectsFullProjectImportIdentityStrategySchema = z.literal('deterministic-v5');
export const projectsFullProjectImportExecutionOutcomeSchema = z.enum([
    'applied',
    'skipped',
    'verified',
]);
export const projectsFullProjectImportExecutionReceiptSchema = z.strictObject({
    sequence: z.number().int().positive(),
    entityType: z.literal(projectsFullProjectImportEntityType),
    sourceId: importText(500),
    sourceSha256: sha256Schema,
    sourceUpdatedAt: importTimestamp,
    targetId: z.string().uuid(),
    identityStrategy: projectsFullProjectImportIdentityStrategySchema,
    outcome: projectsFullProjectImportExecutionOutcomeSchema,
    previousTargetSha256: sha256Schema.nullable(),
    targetSha256: sha256Schema,
    targetCanonical: projectsFullProjectImportTargetPayloadSchema,
    rollbackToken: platformJsonValueSchema,
});
export const projectsFullProjectImportRollbackReceiptSchema = z.strictObject({
    sequence: z.number().int().positive(),
    outcome: z.enum([
        'applied',
        'already-rolled-back',
        'rejected-conflict',
        'rejected-state',
    ]),
    detailCode: z.string().trim().min(3).max(120),
    targetSha256: sha256Schema.nullable(),
});
//# sourceMappingURL=projects-full-project-import.contract.js.map